import React from "react";
import set from "lodash/set";
import { useTranslation } from "react-i18next";

import { PAGES } from "@/internals/constants/pages";
import { NAMESPACES } from "@/internals/constants/namespaces";

import { CtaStyle } from "@/components/Button/types";
import { EventDateTime, EventTimeFormat } from "@/types/qr";
import { TimeOfDayValue } from "@/types/time";

import {
  formatTimeOfDayValueToTimeString,
  parseTimeStringToTimeOfDayValue,
} from "@/utils/timerHelper";
import { EventDateAndTimeView } from "./EventDateAndTimeView";
import "./styles.scss";

export function EventDateAndTime({
  defaultValue,
  onClick,
  ctaStyle,
}: {
  defaultValue: EventDateTime;
  onClick?: (arg: Partial<EventDateTime>) => void;
  ctaStyle?: CtaStyle;
}) {
  useTranslation(PAGES.QR_EVENT);
  useTranslation(NAMESPACES.TIME_PICKER);

  const [format, setTimeFormat] = React.useState<EventTimeFormat>(defaultValue?.format || "AM-PM");
  const [isAllDay, setIsAllDay] = React.useState<boolean>(defaultValue?.allDay || false);

  const startDate = React.useMemo(() => {
    if (!defaultValue?.start?.date) return undefined;
    const parsed = new Date(defaultValue.start.date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [defaultValue?.start?.date]);

  const endDate = React.useMemo(() => {
    if (!defaultValue?.end?.date) return undefined;
    const parsed = new Date(defaultValue.end.date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [defaultValue?.end?.date]);

  const startMinDate = React.useMemo(() => {
    if (!startDate) return "today" as const;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDateOnly = new Date(startDate);
    startDateOnly.setHours(0, 0, 0, 0);

    // When editing, allow displaying an existing past date (Flatpickr clears values < minDate).
    // Keep "today" for new/future dates to preserve the original restriction.
    return startDateOnly < today ? startDateOnly : ("today" as const);
  }, [startDate]);

  // Using a ref prevents effects from creating render loops.
  const onClickRef = React.useRef(onClick);
  React.useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  const startTimeValue = React.useMemo(
    () => parseTimeStringToTimeOfDayValue(defaultValue?.start?.time),
    [defaultValue?.start?.time]
  );
  const endTimeValue = React.useMemo(
    () => parseTimeStringToTimeOfDayValue(defaultValue?.end?.time),
    [defaultValue?.end?.time]
  );

  React.useEffect(() => {
    onClickRef.current?.({ format });
  }, [format]);

  React.useEffect(() => {
    onClickRef.current?.({ allDay: isAllDay });
  }, [isAllDay]);

  const handleInputChange = ({ name, value }: { name: string | string[]; value: unknown }) => {
    if (Array.isArray(name)) {
      const newObject = set({}, name, value);
      return onClickRef.current?.(newObject as Partial<EventDateTime>);
    }

    return onClickRef.current?.({ [name]: value } as Partial<EventDateTime>);
  };

  return (
    <EventDateAndTimeView
      ctaStyle={ctaStyle}
      format={format}
      isAllDay={isAllDay}
      startMinDate={startMinDate}
      startDate={startDate}
      endDate={endDate}
      startTimeValue={startTimeValue}
      endTimeValue={endTimeValue}
      timezone={defaultValue?.timezone}
      actionTitle={defaultValue?.actionTitle}
      onFormatChange={setTimeFormat}
      onAllDayChange={setIsAllDay}
      onStartDateChange={(date) => handleInputChange({ name: ["start", "date"], value: date })}
      onEndDateChange={(date) => handleInputChange({ name: ["end", "date"], value: date })}
      onStartTimeChange={(value?: TimeOfDayValue) =>
        handleInputChange({
          name: ["start", "time"],
          value: formatTimeOfDayValueToTimeString(value),
        })
      }
      onEndTimeChange={(value?: TimeOfDayValue) =>
        handleInputChange({
          name: ["end", "time"],
          value: formatTimeOfDayValueToTimeString(value),
        })
      }
      onClearStartTime={() => handleInputChange({ name: ["start", "time"], value: undefined })}
      onClearEndTime={() => handleInputChange({ name: ["end", "time"], value: undefined })}
      onTimezoneChange={(timezone) => handleInputChange({ name: "timezone", value: timezone })}
      onActionTitleChange={(value) => handleInputChange({ name: "actionTitle", value })}
    />
  );
}
